import { Link } from 'react-router'
import { Wrench, Mail, MapPin, ArrowRight } from 'lucide-react'

const SERVICE_LINKS = [
  { href: '/services/plomberie', label: 'Plomberie' },
  { href: '/services/electricite', label: 'Électricité' },
  { href: '/services/peinture', label: 'Peinture' },
  { href: '/services/renovation', label: 'Rénovation' },
  { href: '/services/menuiserie', label: 'Menuiserie' },
  { href: '/services/toiture', label: 'Toiture' },
  { href: '/services/serrurerie', label: 'Serrurerie' },
  { href: '/services/climatisation', label: 'Climatisation' },
]

const CITY_LINKS = [
  { href: '/services/plomberie/paris', label: 'Paris' },
  { href: '/services/plomberie/lyon', label: 'Lyon' },
  { href: '/services/plomberie/marseille', label: 'Marseille' },
  { href: '/services/plomberie/bordeaux', label: 'Bordeaux' },
  { href: '/services/plomberie/toulouse', label: 'Toulouse' },
  { href: '/services/plomberie/nantes', label: 'Nantes' },
]

interface FooterProps {
  /** Optional contextual links shown above the main footer */
  contextLinks?: Array<{ href: string; label: string }>
  contextTitle?: string
}

export function Footer({ contextLinks, contextTitle }: FooterProps = {}) {
  return (
    <footer className="border-t border-border bg-slate-50">
      {/* Contextual links bar */}
      {contextLinks && contextLinks.length > 0 && (
        <div className="border-b border-border bg-white">
          <div className="mx-auto max-w-7xl px-4 py-4 lg:px-6">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {contextTitle ?? 'Pages similaires'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {contextLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="inline-flex min-h-11 items-center rounded-full border border-border px-3 py-2 text-xs text-slate-600 transition-colors hover:border-primary hover:text-primary"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 py-10 lg:px-6">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
                <Wrench className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-base font-bold text-slate-900">
                Affiche<span className="text-primary">Services</span>
              </span>
            </Link>
            <p className="mt-3 text-xs leading-relaxed text-slate-500">
              Plateforme de mise en relation entre particuliers et artisans vérifiés en France. Devis gratuits sous 48h.
            </p>
            <div className="mt-4 space-y-1.5">
              <a href="mailto:contact@afficheservices.fr" className="flex items-center gap-2 py-1 text-xs text-slate-500 hover:text-primary">
                <Mail className="h-3.5 w-3.5" /> contact@afficheservices.fr
              </a>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <MapPin className="h-3.5 w-3.5" /> Paris, France
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-5">
              <p className="text-xs font-semibold text-slate-900">Restez informé</p>
              <p className="mt-1 text-[11px] text-slate-500">Conseils travaux et offres exclusives</p>
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  const input = e.currentTarget.querySelector('input') as HTMLInputElement | null
                  if (!input?.value) return
                  try {
                    await fetch('/api/newsletter/subscribe', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: input.value, source: 'footer' }),
                    })
                    input.value = ''
                    input.placeholder = 'Merci ! ✓'
                  } catch {
                    input.placeholder = 'Erreur, réessayez'
                  }
                }}
                className="mt-2 flex gap-2"
              >
                <input
                  type="email"
                  required
                  placeholder="votre@email.fr"
                  className="min-h-11 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button type="submit" aria-label="S'abonner à la newsletter" className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary-hover">
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </button>
              </form>
              <p className="mt-1.5 text-xs text-slate-500">Désinscription en un clic. <Link to="/confidentialite" className="underline hover:text-primary">Confidentialité</Link></p>
            </div>

            {/* Social links */}
            <div className="mt-4 flex gap-3">
              {[
                { label: 'Facebook', href: 'https://facebook.com/afficheservices', svg: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                { label: 'Instagram', href: 'https://instagram.com/afficheservices', svg: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
                { label: 'LinkedIn', href: 'https://linkedin.com/company/afficheservices', svg: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-colors hover:bg-primary hover:text-white"
                >
                  <svg aria-hidden="true" className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d={s.svg} /></svg>
                </a>
              ))}
            </div>
          </div>

          {/* Services — collapsible on mobile */}
          <div>
            <details className="group lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Services
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-3 space-y-0.5">
                {SERVICE_LINKS.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </details>
          </div>

          {/* Villes + Outils */}
          <div>
            <details className="group lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Villes
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-3 space-y-0.5">
                {CITY_LINKS.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">{link.label}</Link>
                  </li>
                ))}
                <li>
                  <Link to="/toutes-villes" className="inline-flex min-h-9 items-center py-1.5 text-xs font-semibold text-primary hover:underline">
                    Voir toutes les villes →
                  </Link>
                </li>
              </ul>
            </details>
            <details className="group mt-5 lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Outils
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-2 space-y-0.5">
                <li><Link to="/calculateur" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Calculateur de prix</Link></li>
                <li><Link to="/budget-travaux" className="inline-flex min-h-9 items-center py-1.5 text-xs text-primary hover:text-primary-hover font-medium underline decoration-primary/30">Budget travaux au m²</Link></li>
                <li><Link to="/guide-prix" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Guide des prix</Link></li>
                <li><Link to="/problemes" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Problèmes courants</Link></li>
                <li><Link to="/cee" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Primes CEE</Link></li>
                <li><Link to="/rge" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Artisans RGE</Link></li>
                <li><Link to="/metier" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Fiches métiers</Link></li>
                <li><Link to="/carte-artisans" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Carte artisans</Link></li>
                <li><Link to="/avant-apres" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Avant / Après</Link></li>
                <li><Link to="/barometre" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Baromètre prix</Link></li>
                <li><Link to="/urgence" className="inline-flex min-h-9 items-center py-1.5 text-xs text-red-700 hover:text-red-800 font-medium underline decoration-red-300">Urgences 7j/7</Link></li>
                <li><Link to="/simulateur-aides" className="inline-flex min-h-9 items-center py-1.5 text-xs text-emerald-700 hover:text-emerald-800 font-medium underline decoration-emerald-300">Simulateur aides</Link></li>
              </ul>
            </details>

            {/* Comparatifs — competitor comparison hub for SEO + sales enablement */}
            <details className="group mt-5 lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Comparatifs
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-2 space-y-0.5">
                <li><Link to="/alternatives" className="inline-flex min-h-9 items-center py-1.5 text-xs text-primary hover:text-primary-hover font-medium underline decoration-primary/30">Toutes les alternatives</Link></li>
                <li><Link to="/alternatives/travaux-com" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Alternative à Travaux.com</Link></li>
                <li><Link to="/alternatives/habitatpresto" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Alternative à Habitatpresto</Link></li>
                <li><Link to="/alternatives/123devis" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Alternative à 123Devis</Link></li>
                <li><Link to="/alternatives/allotravaux" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Alternative à AlloTravaux</Link></li>
                <li><Link to="/vs/travaux-com" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">vs Travaux.com</Link></li>
                <li><Link to="/vs/habitatpresto" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">vs Habitatpresto</Link></li>
              </ul>
            </details>
          </div>

          {/* Company + Legal */}
          <div>
            <details className="group lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Entreprise
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-3 space-y-0.5">
                <li><Link to="/a-propos" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">À propos</Link></li>
                <li><Link to="/blog" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Blog</Link></li>
                <li><Link to="/contact" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Contact</Link></li>
                <li><Link to="/devenir-pro" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Devenir partenaire</Link></li>
              <li><Link to="/comment-ca-marche" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Comment ça marche</Link></li>
              <li><Link to="/questions" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Questions aux artisans</Link></li>
              <li><Link to="/partenaires" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Programme partenaire</Link></li>
              <li><Link to="/parrainage" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Parrainage artisans</Link></li>
              <li><Link to="/devis-gratuit" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Devis gratuit express</Link></li>
              <li><Link to="/charte-qualite" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Charte qualité</Link></li>
              <li><Link to="/regions" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Artisans par région</Link></li>
              <li><Link to="/departements" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Artisans par département</Link></li>
              <li><Link to="/toutes-villes" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Toutes les villes</Link></li>
              </ul>
            </details>
            <details className="group mt-5 lg:open" open>
              <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-slate-900 lg:pointer-events-none">
                Légal
                <span className="text-slate-300 transition-transform group-open:rotate-180 lg:hidden">▾</span>
              </summary>
              <ul className="mt-2 space-y-0.5">
                <li><Link to="/mentions-legales" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Confidentialité</Link></li>
                <li><Link to="/cgv" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">CGV</Link></li>
                <li><Link to="/droit-acces" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Droit d'accès</Link></li>
                <li><Link to="/droit-opposition" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Droit d'opposition</Link></li>
                <li><Link to="/violation-donnees" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Violation de données</Link></li>
                <li><Link to="/ademe" className="inline-flex min-h-9 items-center py-1.5 text-xs text-slate-500 hover:text-primary">Données ADEME</Link></li>
              </ul>
            </details>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 lg:px-6">
          <p className="text-[10px] text-slate-500">
            {/* Hardcoded year avoids SSR/CSR hydration mismatch (#418) when
                CF cache serves SSR HTML across year boundary OR when Date
                differs by milliseconds across nodes. Bump yearly at deploy. */}
            © 2026 AfficheServices. Tous droits réservés.
          </p>
          <button
            type="button"
            onClick={() => { if (typeof window !== 'undefined') { localStorage.removeItem('cookie-consent'); window.location.reload() } }}
            className="inline-flex min-h-9 items-center px-2 py-1.5 text-xs text-slate-500 underline hover:text-primary"
          >
            Gérer les cookies
          </button>
        </div>
      </div>
    </footer>
  )
}
